import type { MunicipalityId } from '@enersu/core-orchestrator';

/**
 * Israel Compliance Layer
 *
 * CRITICAL package: implements Israel-specific compliance methodologies.
 *
 * Rules:
 * - NEVER reproduce literal text from SI 5281, SI 5282, SI 1045, ta/9144 docs.
 * - Each compliance check requires a Spec Mapping Document in /docs/specs/.
 * - All public functions must be deterministic and unit-tested.
 */

export {
  CLIMATE_ZONES,
  getClimateZoneProfile,
  listClimateZoneProfiles,
  type ClimateZoneProfile,
  type ClimateZoneSource,
} from './climate-zones';

export {
  computeConstructionUValue,
  computeEnvelopeMean,
  computeEnvelopeUValues,
  ISO_6946_SURFACE_RESISTANCE,
  type EnvelopeMeanResult,
  type SurfaceResistancePair,
  type SurfaceThermalResult,
  type UValue,
} from './envelope';

export {
  checkEnvelopeCompliance,
  type ComplianceStatus,
  type EnvelopeComplianceResult,
  type SurfaceComplianceResult,
  type UmComplianceResult,
} from './si1045-check';

export const VERSION = '0.1.0';

export type IsraelComplianceStudyId =
  | 'sun-hours'
  | 'shadow-study'
  | 'glare-nespach-6'
  | 'solar-rights'
  | 'microclimate-utci'
  | 'daylight-vsc'
  | 'wind-comfort'
  | 'solar-pv-roof'
  | 'embodied-carbon'
  | 'noise'
  | 'thermal-envelope-si-1045'
  | 'green-roof-readiness';

export type SpecMappingStatus = 'missing' | 'validated';

export interface IsraelComplianceStudy {
  id: IsraelComplianceStudyId;
  title: string;
  municipalityIds: readonly MunicipalityId[];
  specPath: `docs/specs/${string}.md`;
  specStatus: SpecMappingStatus;
}

export interface ListPilotComplianceStudiesOptions {
  municipalityId?: MunicipalityId;
}

export class MissingSpecMappingError extends Error {
  constructor(study: IsraelComplianceStudy) {
    super(`Spec Mapping Document is required before implementing ${study.id}.`);
    this.name = 'MissingSpecMappingError';
  }
}

export const PILOT_COMPLIANCE_STUDIES = [
  {
    id: 'sun-hours',
    title: 'Sun hours',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/sun-hours.md',
    specStatus: 'missing',
  },
  {
    id: 'shadow-study',
    title: 'Shadow study',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/shadow-study.md',
    specStatus: 'missing',
  },
  {
    id: 'glare-nespach-6',
    title: 'Glare screening',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/glare-nespach-6.md',
    specStatus: 'missing',
  },
  {
    id: 'solar-rights',
    title: 'Solar rights',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/solar-rights.md',
    specStatus: 'missing',
  },
  {
    id: 'microclimate-utci',
    title: 'Microclimate UTCI',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/microclimate-utci.md',
    specStatus: 'missing',
  },
  {
    id: 'daylight-vsc',
    title: 'Daylight VSC',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/daylight-vsc.md',
    specStatus: 'missing',
  },
  {
    id: 'wind-comfort',
    title: 'Wind comfort',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/wind-comfort.md',
    specStatus: 'missing',
  },
  {
    id: 'solar-pv-roof',
    title: 'Solar PV roof',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/solar-pv-roof.md',
    specStatus: 'missing',
  },
  {
    id: 'embodied-carbon',
    title: 'Embodied carbon',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/embodied-carbon.md',
    specStatus: 'missing',
  },
  {
    id: 'noise',
    title: 'Noise',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/noise.md',
    specStatus: 'missing',
  },
  {
    id: 'thermal-envelope-si-1045',
    title: 'Thermal envelope',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/thermal-envelope-si-1045.md',
    specStatus: 'missing',
  },
  {
    id: 'green-roof-readiness',
    title: 'Green roof readiness',
    municipalityIds: ['tel-aviv'],
    specPath: 'docs/specs/green-roof-readiness.md',
    specStatus: 'missing',
  },
] as const satisfies readonly IsraelComplianceStudy[];

export function placeholder(): string {
  return 'israel-compliance scaffolding ready';
}

export function listPilotComplianceStudies(
  options: ListPilotComplianceStudiesOptions = {},
): readonly IsraelComplianceStudy[] {
  if (options.municipalityId === undefined) {
    return PILOT_COMPLIANCE_STUDIES;
  }

  return PILOT_COMPLIANCE_STUDIES.filter((study) =>
    study.municipalityIds.some((municipalityId) => municipalityId === options.municipalityId),
  );
}

export function getPilotComplianceStudy(
  id: IsraelComplianceStudyId,
): IsraelComplianceStudy | undefined {
  return PILOT_COMPLIANCE_STUDIES.find((study) => study.id === id);
}

export function assertSpecMappingValidated(study: IsraelComplianceStudy): void {
  if (study.specStatus !== 'validated') {
    throw new MissingSpecMappingError(study);
  }
}
